import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Mail, 
  Phone, 
  Building, 
  Calendar, 
  User, 
  MessageSquare, 
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  Trash2,
  Filter,
  Search,
  Download,
  RefreshCw
} from 'lucide-react'
import { useFirestore } from '@hooks/useFirestore'

const EnquiriesManager = () => {
  const enquiriesFirestore = useFirestore('enquiries')
  const [selectedEnquiry, setSelectedEnquiry] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('submittedAt')
  const [sortOrder, setSortOrder] = useState('desc')

  const statusColors = {
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-yellow-100 text-yellow-800',
    qualified: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800'
  }

  const statusIcons = {
    new: AlertCircle,
    contacted: Clock,
    qualified: CheckCircle,
    closed: CheckCircle
  }

  // Filter and sort enquiries
  const filteredEnquiries = enquiriesFirestore.data
    .filter(enquiry => {
      const matchesStatus = statusFilter === 'all' || enquiry.status === statusFilter
      const matchesSearch = !searchTerm || 
        enquiry.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        enquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        enquiry.company?.toLowerCase().includes(searchTerm.toLowerCase())
      return matchesStatus && matchesSearch
    })
    .sort((a, b) => {
      const aValue = a[sortBy] || ''
      const bValue = b[sortBy] || ''
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

  const updateEnquiryStatus = async (enquiryId, newStatus) => {
    try {
      await enquiriesFirestore.updateItem(enquiryId, { 
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
    } catch (error) {
      console.error('Error updating enquiry status:', error)
    }
  }

  const deleteEnquiry = async (enquiryId) => {
    if (confirm('Are you sure you want to delete this enquiry?')) {
      try {
        await enquiriesFirestore.deleteItem(enquiryId)
        if (selectedEnquiry?.id === enquiryId) {
          setSelectedEnquiry(null)
        }
      } catch (error) {
        console.error('Error deleting enquiry:', error)
      }
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const exportEnquiries = () => {
    const csvContent = [
      ['Name', 'Email', 'Company', 'Phone', 'Service', 'Status', 'Submitted At', 'Message'],
      ...filteredEnquiries.map(enquiry => [
        enquiry.fullName || `${enquiry.firstName} ${enquiry.lastName}`,
        enquiry.email,
        enquiry.company,
        enquiry.phone,
        enquiry.service,
        enquiry.status,
        enquiry.submittedAt,
        enquiry.message
      ])
    ].map(row => row.map(field => `"${(field || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `enquiries_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  if (enquiriesFirestore.loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-navy-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted">Loading enquiries...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-display-3 text-navy-900 mb-2">Contact Enquiries</h1>
          <p className="text-body text-muted">Manage and respond to customer inquiries</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={exportEnquiries}
            className="btn-secondary flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
          <button
            onClick={() => window.location.reload()}
            className="btn-secondary flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Total Enquiries', value: enquiriesFirestore.data.length, color: 'from-blue-500 to-blue-600' },
          { label: 'New', value: enquiriesFirestore.data.filter(e => e.status === 'new').length, color: 'from-green-500 to-green-600' },
          { label: 'In Progress', value: enquiriesFirestore.data.filter(e => e.status === 'contacted').length, color: 'from-yellow-500 to-yellow-600' },
          { label: 'Qualified', value: enquiriesFirestore.data.filter(e => e.status === 'qualified').length, color: 'from-purple-500 to-purple-600' }
        ].map((stat, index) => (
          <div key={index} className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted mb-1">{stat.label}</p>
                <p className="text-3xl font-bold text-navy-900">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center`}>
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="Search enquiries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 w-full sm:w-64"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="form-input"
            >
              <option value="submittedAt">Date Submitted</option>
              <option value="fullName">Name</option>
              <option value="company">Company</option>
              <option value="status">Status</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="form-input"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Enquiries List */}
        <div className="space-y-4">
          {filteredEnquiries.length === 0 ? (
            <div className="card p-8 text-center">
              <MessageSquare className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
              <p className="text-muted">No enquiries found</p>
            </div>
          ) : (
            filteredEnquiries.map((enquiry) => {
              const StatusIcon = statusIcons[enquiry.status] || AlertCircle
              return (
                <motion.div
                  key={enquiry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`card p-6 cursor-pointer transition-all duration-200 ${
                    selectedEnquiry?.id === enquiry.id ? 'ring-2 ring-navy-500' : 'hover:shadow-lg'
                  }`}
                  onClick={() => setSelectedEnquiry(enquiry)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-navy-900 mb-1">
                        {enquiry.fullName || `${enquiry.firstName} ${enquiry.lastName}`}
                      </h3>
                      <p className="text-sm text-muted mb-2">{enquiry.company}</p>
                      <div className="flex items-center space-x-4 text-sm text-muted">
                        <span className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {enquiry.email}
                        </span>
                        {enquiry.phone && (
                          <span className="flex items-center">
                            <Phone className="w-4 h-4 mr-1" />
                            {enquiry.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[enquiry.status]}`}>
                        <StatusIcon className="w-3 h-3 inline mr-1" />
                        {enquiry.status}
                      </span>
                      <span className="text-xs text-muted">
                        {formatDate(enquiry.submittedAt)}
                      </span>
                    </div>
                  </div>
                  
                  {enquiry.service && (
                    <div className="mb-3">
                      <span className="inline-block bg-neutral-100 text-neutral-700 px-2 py-1 rounded text-xs">
                        {enquiry.service}
                      </span>
                    </div>
                  )}
                  
                  {enquiry.message && (
                    <p className="text-sm text-neutral-600 line-clamp-2">
                      {enquiry.message}
                    </p>
                  )}
                </motion.div>
              )
            })
          )}
        </div>

        {/* Enquiry Details */}
        <div className="lg:sticky lg:top-8">
          {selectedEnquiry ? (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-heading-2 text-navy-900">Enquiry Details</h2>
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedEnquiry.status}
                    onChange={(e) => updateEnquiryStatus(selectedEnquiry.id, e.target.value)}
                    className="form-input text-sm"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="closed">Closed</option>
                  </select>
                  <button
                    onClick={() => deleteEnquiry(selectedEnquiry.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-navy-900 mb-3">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-neutral-500 mr-3" />
                      <span>{selectedEnquiry.fullName || `${selectedEnquiry.firstName} ${selectedEnquiry.lastName}`}</span>
                    </div>
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-neutral-500 mr-3" />
                      <a href={`mailto:${selectedEnquiry.email}`} className="text-navy-600 hover:underline">
                        {selectedEnquiry.email}
                      </a>
                    </div>
                    {selectedEnquiry.phone && (
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 text-neutral-500 mr-3" />
                        <a href={`tel:${selectedEnquiry.phone}`} className="text-navy-600 hover:underline">
                          {selectedEnquiry.phone}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Building className="w-4 h-4 text-neutral-500 mr-3" />
                      <span>{selectedEnquiry.company}</span>
                    </div>
                    {selectedEnquiry.title && (
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-neutral-500 mr-3" />
                        <span>{selectedEnquiry.title}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-navy-900 mb-3">Company Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedEnquiry.companySize && (
                      <div>
                        <span className="text-muted">Size:</span>
                        <p className="font-medium">{selectedEnquiry.companySize}</p>
                      </div>
                    )}
                    {selectedEnquiry.industry && (
                      <div>
                        <span className="text-muted">Industry:</span>
                        <p className="font-medium">{selectedEnquiry.industry}</p>
                      </div>
                    )}
                    {selectedEnquiry.service && (
                      <div>
                        <span className="text-muted">Service Interest:</span>
                        <p className="font-medium">{selectedEnquiry.service}</p>
                      </div>
                    )}
                    {selectedEnquiry.timeline && (
                      <div>
                        <span className="text-muted">Timeline:</span>
                        <p className="font-medium">{selectedEnquiry.timeline}</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedEnquiry.message && (
                  <div>
                    <h3 className="font-semibold text-navy-900 mb-3">Message</h3>
                    <div className="bg-neutral-50 p-4 rounded-lg">
                      <p className="text-sm whitespace-pre-wrap">{selectedEnquiry.message}</p>
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="font-semibold text-navy-900 mb-3">Submission Details</h3>
                  <div className="text-sm space-y-2">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-neutral-500 mr-3" />
                      <span>Submitted: {formatDate(selectedEnquiry.submittedAt)}</span>
                    </div>
                    {selectedEnquiry.newsletter && (
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
                        <span>Subscribed to newsletter</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <a
                    href={`mailto:${selectedEnquiry.email}?subject=Re: Your inquiry about ${selectedEnquiry.service || 'our services'}&body=Hi ${selectedEnquiry.firstName || selectedEnquiry.fullName},%0D%0A%0D%0AThank you for your inquiry about our services.%0D%0A%0D%0A`}
                    className="btn-primary w-full justify-center"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Reply via Email
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="card p-8 text-center">
              <Eye className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
              <p className="text-muted">Select an enquiry to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EnquiriesManager 